import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';
import DeliveryNotesList from './components/DeliveryNotesList';
import DeliveryNoteForm from './components/DeliveryNoteForm';
import NotePreview from './components/NotePreview';
import NotesFilters from './components/NotesFilters';
import BulkNotesOperations from './components/BulkNotesOperations';
import Icon from '../../components/AppIcon';


const DeliveryNotesManagement = () => {
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for delivery notes
  const mockDeliveryNotes = [
    {
      id: 'ALB-2024-001',
      orderId: 'ORD-2024-001',
      customerName: 'Tech Solutions S.L.',
      customerContact: 'pedidos@techsolutions.es',
      deliveryDate: new Date('2024-12-14'),
      createdDate: new Date('2024-12-11'),
      status: 'completed',
      products: [
        { name: 'Laptop Dell XPS 13', quantity: 2, delivered: 2, sku: 'LAP-DELL-001' },
        { name: 'Monitor LG UltraWide 34"', quantity: 1, delivered: 1, sku: 'MON-LG-004' }
      ],
      deliveryAddress: {
        street: 'Calle Gran Vía 28',
        city: 'Madrid',
        postalCode: '28013',
        country: 'España'
      },
      instructions: 'Entregar en recepción durante horario de oficina',
      driverName: 'Carlos Martínez',
      vehiclePlate: '1234-ABC',
      signedBy: 'Ana López - Recepcionista',
      deliveryPhoto: 'photo_url_1.jpg',
      gpsLocation: { lat: 40.4168, lng: -3.7038 },
      deliveryTime: new Date('2024-12-14T15:30:00'),
      notes: 'Entrega realizada sin incidencias',
      isPartialDelivery: false
    },
    {
      id: 'ALB-2024-002',
      orderId: 'ORD-2024-002',
      customerName: 'Innovación Digital',
      customerContact: 'compras@innovaciondigital.com',
      deliveryDate: new Date('2024-12-13'),
      createdDate: new Date('2024-12-10'),
      status: 'pending',
      products: [
        { name: 'iPhone 15 Pro', quantity: 5, delivered: 0, sku: 'PHN-IPH-002' },
        { name: 'Teclado Logitech MX Keys', quantity: 5, delivered: 0, sku: 'KEY-LOG-005' }
      ],
      deliveryAddress: {
        street: 'Avenida Diagonal 500',
        city: 'Barcelona',
        postalCode: '08006',
        country: 'España'
      },
      instructions: 'Llamar al timbre del segundo piso',
      driverName: 'Miguel García',
      vehiclePlate: '5678-DEF',
      signedBy: null,
      deliveryPhoto: null,
      gpsLocation: null,
      deliveryTime: null,
      notes: '',
      isPartialDelivery: false
    },
    {
      id: 'ALB-2024-003',
      orderId: 'ORD-2024-004',
      customerName: 'Consultoría Madrid',
      customerContact: 'admin@consultoriamadrid.es',
      deliveryDate: new Date('2024-12-12'),
      createdDate: new Date('2024-12-09'),
      status: 'completed',
      products: [
        { name: 'Laptop Dell XPS 13', quantity: 1, delivered: 1, sku: 'LAP-DELL-001' },
        { name: 'Teclado Logitech MX Keys', quantity: 1, delivered: 1, sku: 'KEY-LOG-005' }
      ],
      deliveryAddress: {
        street: 'Calle Serrano 125',
        city: 'Madrid',
        postalCode: '28006',
        country: 'España'
      },
      instructions: 'Acceso por garage, planta -1',
      driverName: 'Roberto Silva',
      vehiclePlate: '9012-GHI',
      signedBy: 'María García - Recepcionista',
      deliveryPhoto: 'photo_url_2.jpg',
      gpsLocation: { lat: 40.4378, lng: -3.6795 },
      deliveryTime: new Date('2024-12-12T11:15:00'),
      notes: 'Cliente muy satisfecho con la entrega',
      isPartialDelivery: false
    },
    {
      id: 'ALB-2024-004',
      orderId: 'ORD-2024-005',
      customerName: 'StartUp Valencia',
      customerContact: 'office@startupvalencia.com',
      deliveryDate: new Date('2024-12-15'),
      createdDate: new Date('2024-12-12'),
      status: 'in_progress',
      products: [
        { name: 'Monitor LG UltraWide 34"', quantity: 4, delivered: 2, sku: 'MON-LG-004' }
      ],
      deliveryAddress: {
        street: 'Ciudad de las Artes y las Ciencias',
        city: 'Valencia',
        postalCode: '46013',
        country: 'España'
      },
      instructions: 'Entrega parcial autorizada',
      driverName: 'Juan López',
      vehiclePlate: '3456-JKL',
      signedBy: 'Pedro Ruiz - Coordinador',
      deliveryPhoto: 'photo_url_3.jpg',
      gpsLocation: { lat: 39.4550, lng: -0.3504 },
      deliveryTime: new Date('2024-12-15T09:45:00'),
      notes: 'Entrega parcial - resto programado para mañana',
      isPartialDelivery: true,
      remainingProducts: [
        { name: 'Monitor LG UltraWide 34"', quantity: 2, sku: 'MON-LG-004' }
      ]
    }
  ];

  // Mock orders data for creating new delivery notes
  const mockOrders = [
    {
      id: 'ORD-2024-006',
      customerName: 'Empresa de Tecnología',
      products: [
        { name: 'Samsung Galaxy Tab S9', quantity: 3, sku: 'TAB-SAM-003' }
      ],
      deliveryAddress: {
        street: 'Polígono Industrial Sur',
        city: 'Málaga',
        postalCode: '29004',
        country: 'España'
      }
    }
  ];

  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      setDeliveryNotes(mockDeliveryNotes);
      setFilteredNotes(mockDeliveryNotes);
      setIsLoading(false);
    }, 800);
  }, []);

  // Filter logic
  useEffect(() => {
    let filtered = [...deliveryNotes];

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(note => note.status === filterStatus);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(note =>
        note.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.customerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date range filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(note => {
        const deliveryDate = new Date(note.deliveryDate);
        return deliveryDate >= dateRange.start && deliveryDate <= dateRange.end;
      });
    }

    setFilteredNotes(filtered);
  }, [deliveryNotes, filterStatus, searchTerm, dateRange]);

  const handleCreateNote = (noteData) => {
    const newNote = {
      id: `ALB-2024-${String(deliveryNotes.length + 1).padStart(3, '0')}`,
      ...noteData,
      createdDate: new Date(),
      status: 'pending'
    };

    setDeliveryNotes(prev => [newNote, ...prev]);
    setIsCreateMode(false);
  };

  const handleUpdateNote = (noteId, updates) => {
    setDeliveryNotes(prev =>
      prev.map(note =>
        note.id === noteId ? { ...note, ...updates } : note
      )
    );
    setEditingNote(null);
  };

  const handleCompleteDelivery = (noteId, deliveryData) => {
    setDeliveryNotes(prev =>
      prev.map(note => {
        if (note.id === noteId) {
          return {
            ...note,
            status: 'completed',
            deliveryTime: new Date(),
            ...deliveryData
          };
        }
        return note;
      })
    );
  };

  const handlePartialDelivery = (noteId, deliveredQuantities, remainingProducts) => {
    setDeliveryNotes(prev =>
      prev.map(note => {
        if (note.id === noteId) {
          const updatedProducts = note.products.map(product => {
            const delivered = deliveredQuantities[product.sku] || 0;
            return { ...product, delivered };
          });

          return {
            ...note,
            status: 'in_progress',
            products: updatedProducts,
            isPartialDelivery: true,
            remainingProducts,
            deliveryTime: new Date()
          };
        }
        return note;
      })
    );

    // Create new delivery note for remaining products
    if (remainingProducts.length > 0) {
      const originalNote = deliveryNotes.find(n => n.id === noteId);
      const newNote = {
        ...originalNote,
        id: `ALB-2024-${String(deliveryNotes.length + 2).padStart(3, '0')}`,
        products: remainingProducts,
        createdDate: new Date(),
        status: 'pending',
        isPartialDelivery: false,
        deliveryTime: null,
        signedBy: null,
        deliveryPhoto: null
      };
      
      setDeliveryNotes(prev => [newNote, ...prev]);
    }
  };

  const handleNoteSelect = (noteId) => {
    setSelectedNotes(prev => {
      if (prev.includes(noteId)) {
        return prev.filter(id => id !== noteId);
      } else {
        return [...prev, noteId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedNotes.length === filteredNotes.length) {
      setSelectedNotes([]);
    } else {
      setSelectedNotes(filteredNotes.map(note => note.id));
    }
  };

  const handleBulkPrint = () => {
    console.log('Printing delivery notes:', selectedNotes);
    // Implement bulk printing logic
    setSelectedNotes([]);
  };

  const handleBulkStatusUpdate = (status) => {
    setDeliveryNotes(prev =>
      prev.map(note =>
        selectedNotes.includes(note.id) ? { ...note, status } : note
      )
    );
    setSelectedNotes([]);
  };

  const getStatusStats = () => {
    const stats = {
      pending: deliveryNotes.filter(n => n.status === 'pending').length,
      in_progress: deliveryNotes.filter(n => n.status === 'in_progress').length,
      completed: deliveryNotes.filter(n => n.status === 'completed').length
    };
    return stats;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-heading font-semibold text-text-primary mb-2">
                  Gestión de Albaranes
                </h1>
                <p className="text-text-muted">
                  Crea y gestiona la documentación de entrega para los envíos
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setIsCreateMode(true)}
                  className="inline-flex items-center px-4 py-2 bg-primary text-white text-sm font-body-medium rounded-lg hover:bg-primary-600 transition-micro"
                >
                  <Icon name="Plus" size={16} className="mr-2" />
                  Nuevo Albarán
                </button>
              </div>
            </div>
          </div>

          {/* Status Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">Pendientes</p>
                  <p className="text-2xl font-heading-semibold text-warning">
                    {getStatusStats().pending}
                  </p>
                </div>
                <Icon name="Clock" size={24} className="text-warning" />
              </div>
            </div>
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">En progreso</p>
                  <p className="text-2xl font-heading-semibold text-primary">
                    {getStatusStats().in_progress}
                  </p>
                </div>
                <Icon name="Truck" size={24} className="text-primary" />
              </div>
            </div>
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">Completados</p>
                  <p className="text-2xl font-heading-semibold text-success">
                    {getStatusStats().completed}
                  </p>
                </div>
                <Icon name="CheckCircle" size={24} className="text-success" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <NotesFilters
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              dateRange={dateRange}
              setDateRange={setDateRange}
              totalNotes={deliveryNotes.length}
              filteredNotes={filteredNotes.length}
            />
          </div>

          {/* Main Content */}
          {isCreateMode ? (
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 lg:col-span-7">
                <DeliveryNoteForm
                  orders={mockOrders}
                  onSubmit={handleCreateNote}
                  onCancel={() => setIsCreateMode(false)}
                />
              </div>
              <div className="col-span-12 lg:col-span-5">
                <NotePreview />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 lg:col-span-5">
                <DeliveryNotesList
                  notes={filteredNotes}
                  selectedNotes={selectedNotes}
                  onNoteSelect={handleNoteSelect}
                  onSelectAll={handleSelectAll}
                  onRowClick={setSelectedNote}
                  onCompleteDelivery={handleCompleteDelivery}
                  onPartialDelivery={handlePartialDelivery}
                  isLoading={isLoading}
                />
              </div>
              <div className="col-span-12 lg:col-span-7">
                <NotePreview
                  selectedNote={selectedNote}
                  onClose={() => setSelectedNote(null)}
                  onEdit={setEditingNote}
                  onCompleteDelivery={handleCompleteDelivery}
                  onPartialDelivery={handlePartialDelivery}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Bulk Operations */}
      <BulkNotesOperations
        selectedCount={selectedNotes.length}
        onBulkPrint={handleBulkPrint}
        onBulkStatusUpdate={handleBulkStatusUpdate}
        onClearSelection={() => setSelectedNotes([])}
      />

      <QuickActionFAB />
    </div>
  );
};

export default DeliveryNotesManagement;